import { useEffect, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import Modal from '../Modal';
import LoginModal from '../../containers/LoginModal';
import Notification from '../../containers/Notification';
import CallScreenControls from '../../containers/CallScreenControls';
import QBLogo from '../../../images/qb-logo-horizontal.svg';
import ShareImg from '../../../images/share.svg';
import {
  AttachPluginActionCreator,
  CreateSessionActionCreator,
  JoinRoomActionCreator,
  ListRoomParticipantsActionCreator,
  ShowNotificationActionCreator,
  ToggleScreenSharingActionCreator,
} from '../../actionCreators';
import { ListRoomParticipantsSuccessAction } from '../../actions';
import './styles.css';

export type CallScreenStateProps = {
  loggedIn: boolean;
  participants: Participant[];
  screenshare: boolean;
  user: any;
};

export type CallScreenDispatchProps = {
  attachPlugin: AttachPluginActionCreator;
  createSession: CreateSessionActionCreator;
  joinRoom: JoinRoomActionCreator;
  listRoomParticipants: ListRoomParticipantsActionCreator;
  showNotification: ShowNotificationActionCreator;
  toggleScreenSharing: ToggleScreenSharingActionCreator;
};

type CallScreenProps = RouteComponentProps &
  CallScreenStateProps &
  CallScreenDispatchProps;

const copyLink = () => {
  const selection = document.getSelection();
  const range =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const el = document.createElement('textarea');
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-300%';
  el.value = window.location.href;
  document.body.appendChild(el);
  el.select();
  el.setSelectionRange(0, 9999);
  document.execCommand('copy');
  document.body.removeChild(el);
  if (selection && range) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

export default function CallScreen(props: CallScreenProps): JSX.Element {
  const {
    attachPlugin,
    createSession,
    joinRoom,
    listRoomParticipants,
    location,
    loggedIn,
    participants,
    screenshare,
    showNotification,
    toggleScreenSharing,
    user,
  } = props;

  const localVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (loggedIn) {
      const query = new URLSearchParams(location.search);
      const roomId = query.get('id');
      createSession(() =>
        attachPlugin({
          isRemote: false,
          userId: null,
          then: () => {
            roomId &&
              joinRoom({
                roomId,
                then: () =>
                  listRoomParticipants(
                    (action: ListRoomParticipantsSuccessAction) => {
                      action.payload.forEach((participant) =>
                        attachPlugin({
                          isRemote: true,
                          userId: participant.id,
                        })
                      );
                    }
                  ),
              });
          },
        })
      );
    }
  }, [loggedIn]);

  useEffect(() => {
    if (screenshare && localVideoRef.current) {
      const stream = localVideoRef.current.srcObject;
      if (stream && stream instanceof MediaStream) {
        const track = stream.getVideoTracks()[0];
        if (track) {
          track.onended = toggleScreenSharing;
        }
      }
    }
  }, [screenshare]);

  const copyShareLink = () => {
    copyLink();
    showNotification({ message: 'Link copied' });
  };

  const username = user ? user.full_name || user.login || user.email : '';
  return (
    <div className="call-screen">
      <Modal>{loggedIn ? <Notification /> : <LoginModal />}</Modal>
      <header>
        <img className="logo" src={QBLogo} />
        <div className="username">{username}</div>
        <div className="link">
          <span className="muted">Call link:&nbsp;</span>
          <span className="href">{window.location.href}</span>
          <button className="share" onClick={copyShareLink} type="button">
            <img src={ShareImg} />
            Share Link
          </button>
        </div>
      </header>
      <main>
        <div className="videos">
          <video
            autoPlay
            className={screenshare ? '' : 'mirror'}
            id="local"
            muted
            playsInline
            ref={localVideoRef}
          >
            Your browser does not support HTML5 video element
          </video>
          {participants.map(({ id }) => (
            <video autoPlay id={`remote${id}`} key={id} playsInline />
          ))}
        </div>
        <CallScreenControls />
      </main>
    </div>
  );
}
